/**
 * PublisherHero — the Publisher POV. Publisher, franchise or title: one hero.
 *
 * Built to `devourplay-anchor/Drop-In/team-drops/publisher-drop.html`.
 *
 * ONE DESIGN, and a launch is a LAYER on it — not a different page.
 *
 * The page is the Drop's own identity and content: header image, mark, wordmark,
 * credit line, socials, follow, tagline, synopsis, platforms, buy link, trailer,
 * video strip, modes rail. Activision, Call of Duty and Modern Warfare 4 all
 * have every one of those, and they render identically on all three.
 *
 * A LAUNCH adds exactly four things and changes one word:
 *   1. the status pill        ("Launching 23 Oct", "Beta", "Out now")
 *   2. the release-date line
 *   3. the countdown
 *   4. beta markers on the rail
 *   · and the CTA reads "Pre-order" instead of "Get the game"
 *
 * That is the whole difference, which is why `release` carries two fields and
 * everything else is a Drop prop. Driving the hero off `release` is what made a
 * shipped franchise render a full-height black void with its own banner sitting
 * unused on the record — and then, once the art was hoisted, still show no
 * modes, no videos and no platforms for the same reason one level down.
 *
 * There is no composition difference left. Every block renders when it has
 * something to render and is absent when it does not — the countdown when there
 * is a date, and the store link when there is a URL. None of them is another's
 * fallback.
 *
 * A franchise's GAMES are not in here. They belong to the "Other Games"
 * section, which already renders children before siblings — a rail in the hero
 * was a second copy of that list, sitting over the artwork.
 *
 * Deliberately NOT here: an editions/price table. We are not a storefront;
 * pre-order links out to the publisher's own commerce.
 */

import React, {useState} from "react";
import {isSafeHref, safeCssUrl} from "./play/safeHref";
import {socialDeeplink, PlatformGlyph} from "./DropHero";
import {DropFollowButton} from "./DropFollowButton";
import {PublisherHeroWatch, type DropVideoView} from "./PublisherHeroWatch";
import "./PublisherHero.scss";
import {formatCompactNumber} from "@/utils/formatCompactNumber";

/**
 * The LAUNCH LAYER — a date and a phase, and nothing else.
 *
 * Everything this interface used to declare (title, tagline, synopsis,
 * platforms, trailer, videos) is a Drop-level prop below, because a shipped
 * title and a franchise have all of it and no launch at all.
 */
export interface DropReleaseView {
    /** Epoch ms. Absent while a title is announced with no date. */
    releaseDate?: number;
    status?: "ANNOUNCED" | "BETA" | "RELEASED";
}

/** One social channel chip in the identity row. */
export interface PublisherHeroSocial {
    platform: string;
    /** Follower count as reported by the platform parser. */
    followerCount?: number;
    profileUrl?: string;
}

interface PublisherHeroProps {
    release: DropReleaseView;
    /** Publisher/brand name shown in the identity block — e.g. "Call of Duty". */
    brandName?: string;
    /**
     * Credit line — studio, publisher, whoever made it.
     *
     * The anchor reads "Activision · Infinity Ward · Call of Duty ↗": the
     * people behind the title, then the franchise it belongs to as a link up.
     * The franchise half comes from `parentName`/`parentHref` below rather
     * than being typed into this string, so it stays right when the parent is
     * renamed and links to a page that actually exists.
     */
    creditLine?: string;
    /** The parent Drop's name — the last segment of the credit line. */
    parentName?: string;
    /** The parent's page, when it has one. A parent may exist only to group its children. */
    parentHref?: string;
    /** Square icon rendered beside the name — the Drop's avatar. */
    markUrl?: string;
    /**
     * Wide wordmark lockup, rendered large in the body.
     *
     * NOT interchangeable with `markUrl`. An icon is square and identifies the
     * Drop in a list; a lockup is horizontal and is how a visitor reads the
     * page. Falls back to the title set as type when a Drop has no wordmark.
     */
    logoImageUrl?: string;
    /**
     * The hero image. THE hero image — the same one every other layout uses.
     *
     * Resolved by the caller exactly as `Hero.tsx` does:
     * `heroSection.heroImageUrl || creatorHeaderImageUrl`. A publisher hero is
     * not a different hero, and this component previously read a `release.keyArtUrl`
     * that existed only because the publisher POV added a second field beside
     * the one every Drop already had.
     */
    heroImageUrl?: string;
    /** Poster for the trailer. Click-through opens the real thing. */
    trailerPosterUrl?: string;
    /**
     * Line under the player. Omit and it is derived from the roster — the
     * anchor's "38 official creators co-streaming the reveal right now",
     * which is the number that makes the trailer feel like an event rather
     * than a video. Pass a value to override.
     */
    watchNote?: string;
    /** Where the buy/play CTA goes. Absent ⇒ the button is not rendered at all. */
    purchaseUrl?: string;
    /** The feature video. True of a shipped game as much as an unreleased one. */
    trailerUrl?: string;
    /**
     * The strip under the feature — videos the publisher assigned.
     *
     * Assigned rather than derived because official highlights are not
     * necessarily on YouTube: a publisher may keep them on Twitch or Kick, or
     * want three specific ones rather than the three most recent. When this is
     * empty the hero falls back to the Drop's connected YouTube channel, so a
     * publisher who has not curated anything still gets a populated strip.
     */
    videos?: DropVideoView[];
    /**
     * What this Drop is about.
     *
     * A tagline and a synopsis are true of a shipped game as much as an
     * unreleased one — they were on `release`, which left a Drop with nothing
     * launching unable to say what it is. See
     * knowledge/projects/active/drop-identity-collapse/BUILD_PLAN.md.
     */
    tagline?: string;
    synopsis?: string;
    /**
     * Platforms it runs on — "PS5 · PC · Nintendo Switch 2".
     *
     * Rendered on the same line as the release date, which is the only reason
     * this ever looked like launch information. A shipped game runs on
     * platforms too.
     */
    platforms?: string[];
    /** Verified publisher — renders the tick beside the brand name. */
    verified?: boolean;
    /** Official channels, shown as follower chips in the identity row. */
    socials?: PublisherHeroSocial[];
    /** Slug the follow control acts on. Absent ⇒ no follow button. */
    followSlug?: string;
    /**
     * CTA colours from the Drop's hero config, when an operator has set them.
     *
     * The button was styled entirely in the stylesheet — background from the
     * Drop's accent, ink hardcoded — so the editor's "CTA Background" and "CTA
     * Text Color" fields did nothing on a publisher page. Setting the text to
     * black changed nothing and there was no way to tell why, because the
     * fields belong to the generic hero CTA and this is a different button.
     *
     * Passed through as CSS custom properties rather than inline styles on the
     * element, so the stylesheet keeps owning layout and hover behaviour and
     * only the two colours are overridable. Both fall back to what the sheet
     * already used, so an unconfigured Drop looks exactly as it did.
     */
    ctaBackground?: string;
    ctaTextColor?: string;
    onLoginRequired?: () => void;
    referralUserId?: string;
}

/** 21_400_000 → "21.4M". Chips are narrow; exact counts do not fit or matter. */

/** Whole days until release. Negative once shipped, in which case we don't count. */
function daysUntil(releaseDate?: number): number | null {
    if (!releaseDate) return null;
    const ms = releaseDate - Date.now();
    if (ms <= 0) return null;
    return Math.ceil(ms / 86_400_000);
}

function formatDate(releaseDate?: number): string | null {
    if (!releaseDate) return null;
    return new Date(releaseDate).toLocaleDateString(undefined, {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
}

/** ANNOUNCED and BETA titles lead with the date; a shipped one leads with "out now". */
/** A launch is news for about a month. After that it is just the game. */
const OUT_NOW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function flagLabel(status: DropReleaseView["status"], releaseDate?: number): string | undefined {
    /*
     * "Out now" only while it is actually news.
     *
     * RELEASED is a permanent state, so this pill sat over Call of Duty — a
     * franchise that has been out for two decades — announcing that it is
     * available. Launch-window language on an established title reads as copy
     * nobody maintained.
     *
     * Inside the window it is worth saying; outside it, or with no date to
     * judge by, the hero renders no pill. Same principle as the bare ANNOUNCED
     * case below: a status we assigned ourselves is not news.
     */
    if (status === "RELEASED") {
        if (!releaseDate) return undefined;
        return Date.now() - releaseDate <= OUT_NOW_WINDOW_MS ? "Out now" : undefined;
    }
    const date = formatDate(releaseDate);
    if (status === "BETA") return date ? `Beta · launches ${date}` : "In beta";
    /*
     * A bare "Announced" is not a status worth a pill.
     *
     * `status` DEFAULTS to ANNOUNCED, so any Drop carrying a release at all
     * got flagged — a shipped franchise like Call of Duty wore
     * "Announced" over its own key art. With a date it is real news and says
     * when; without one it is a default we assigned ourselves, and the hero
     * renders no pill at all.
     */
    return date ? `Launching ${date}` : undefined;
}

export function PublisherHero({
    release, brandName, creditLine, parentName, parentHref, markUrl, logoImageUrl, trailerPosterUrl,
    watchNote, purchaseUrl, verified, socials = [], followSlug, heroImageUrl,
    tagline, synopsis, platforms, videos, trailerUrl,
    ctaBackground, ctaTextColor, onLoginRequired, referralUserId,
}: PublisherHeroProps): React.ReactElement {
    const days = daysUntil(release.releaseDate);

    /* One hero image, resolved by the caller. No second source. */
    const heroArtUrl = heroImageUrl;

    /*
     * The wide wordmark, not the square icon — `markUrl` identifies the Drop in
     * a list and letterboxes badly at this size. Falls through to nothing so the
     * title below renders as type.
     */
    const lockupSrc = logoImageUrl;

    /*
     * What the page is called: the Drop's own name.
     *
     * There is no second title field. A release used to carry one, which meant
     * the page's name and the game's name could disagree and a franchise with
     * no launch had neither — the name an operator typed is the name every
     * other surface already shows, so it is the one used here.
     */
    const gameTitle = brandName ?? "";


    /*
     * A wordmark that 404s must fall back to type, not to a broken-image glyph
     * where the title goes. These URLs are frequently pasted rather than
     * uploaded, so the failure is a routine one.
     */
    const [markFailed, setMarkFailed] = useState(false);


    /*
     * Is there a game to show, or only an identity?
     *
     * A Drop created minutes ago has a name, a mark and its channels and
     * nothing else. The hero reserves 600px and pushes its two halves apart, so
     * with nothing in the middle it rendered a full screen of black with the
     * title stranded at the bottom — the page looked broken at exactly the
     * moment an operator was deciding whether it worked.
     *
     * Collapsed, the same hero is an identity band: correct, quiet, and
     * obviously waiting for content rather than failing to load it.
     */
    const hasGameContent = Boolean(heroArtUrl ||
        lockupSrc ||
        tagline ||
        synopsis ||
        trailerUrl ||
        purchaseUrl ||
        release.releaseDate ||
        platforms?.length);

    const heroStyle = heroArtUrl ? {backgroundImage: safeCssUrl(heroArtUrl)} : undefined;

    /*
     * Only the keys an operator actually set. An undefined custom property
     * would blank the value rather than fall through to the stylesheet's own,
     * so an unconfigured Drop must send nothing at all.
     */
    const themeStyle: React.CSSProperties = {
        ...ctaBackground ? {"--ph-cta-bg": ctaBackground} : {},
        ...ctaTextColor ? {"--ph-cta-ink": ctaTextColor} : {},
    } as React.CSSProperties;

    /*
     * These URLs are publisher-authored and reach the page from stored data, so
     * they get the same guard every other href-bearing widget uses: a
     * `javascript:` or protocol-relative value is dropped rather than rendered.
     * See play/safeHref.ts.
     *
     * Declared above every use — a `const` referenced before its declaration
     * is a temporal-dead-zone crash, not a hoisted undefined, and this file has
     * shipped that bug three times.
     */
    const buyHref = isSafeHref(purchaseUrl) ? purchaseUrl : undefined;
    /*
     * Only to decide whether the hero reserves a right-hand column. The player
     * itself validates its own URL — see PublisherHeroWatch, which returns null
     * on an unsafe or absent one, so the two agree by construction.
     */
    const hasWatchPanel = isSafeHref(trailerUrl);

    return (
        <section
            className={`pub-hero${hasGameContent ? "" : " pub-hero--bare"}${
                hasWatchPanel ? "" : " pub-hero--no-watch"}`}
            aria-label={gameTitle}
            style={themeStyle}
        >
            <span className="pub-hero__art" style={heroStyle} />
            <span className="pub-hero__scrim" />
            <span className="pub-hero__grain" />

            {/* Identity sits inside the art — for a publisher the game IS the
                identity, so it never gets a strip of its own above the hero. */}
            <div className="pub-hero__top">
                <div className="pub-hero__identity">
                    {/*
                      * The Drop's icon. `markUrl` was destructured, documented
                      * and passed by the caller, and then never rendered — so
                      * the identity row was a bare name, and on a franchise
                      * with no wordmark that name appeared twice with nothing
                      * to tell the two instances apart.
                      */}
                    {markUrl &&
                        <img className="pub-hero__mark" src={markUrl} alt="" loading="lazy" />
                    }
                    <span>
                        <span className="pub-hero__brand">
                            {brandName ?? gameTitle}
                            {verified && <span className="pub-hero__tick" title="Verified publisher">✓</span>}
                        </span>
                        {/*
                          * "Activision · Infinity Ward · Call of Duty ↗" — the
                          * credits, then the franchise as a link up to its
                          * Drop. The parent is appended rather than typed into
                          * the credit line so it follows a rename and only
                          * becomes a link when that page is actually public.
                          */}
                        {(creditLine || parentName) &&
                            <span className="pub-hero__studio">
                                {creditLine}
                                {creditLine && parentName && " · "}
                                {parentName && (parentHref
                                    ? <a className="pub-hero__studio-link" href={parentHref}>{parentName} ↗</a>
                                    : parentName
                                )}
                            </span>
                        }
                    </span>
                </div>

                {/* Official channels and the follow, per the anchor's identity
                    row. The follow is the SAME one offered above the roster —
                    see DropFollowButton. */}
                {(socials.length > 0 || followSlug) &&
                    <div className="pub-hero__socials">
                        {socials.map((sc) => {
                            /*
                             * `socialDeeplink`, not `profileUrl` alone.
                             *
                             * Most connected socials store a username and no
                             * profileUrl — the platform parsers write the
                             * handle, and the URL is derived. Reading only
                             * profileUrl meant every chip fell to the `span`
                             * branch, so the whole row rendered as dead text
                             * that looked exactly like links. DropHero has
                             * built these URLs from the handle since it
                             * shipped; this is that function, not a second one.
                             */
                            const resolved = socialDeeplink(sc);
                            const href = isSafeHref(resolved) ? resolved : undefined;
                            const Chip = href ? "a" : "span";
                            return (
                                <Chip
                                    key={sc.platform}
                                    className={`pub-hero__social pub-hero__social--${sc.platform.toLowerCase()}`}
                                    {...(href ? {href, target: "_blank", rel: "noopener noreferrer"} : {})}
                                >
                                    {/*
                                      * The platform's OWN glyph — `PlatformGlyph`
                                      * from DropHero, the same component the
                                      * creator page uses.
                                      *
                                      * This was a coloured dot, copied from the
                                      * anchor's `<span class="so__dot">`. The
                                      * anchor hardcodes three known platforms and
                                      * can colour-code them; a real Drop carries
                                      * six, and the palette only distinguishes
                                      * four — so the row rendered as identical
                                      * grey dots that named nothing and linked
                                      * somewhere unguessable.
                                      *
                                      * Third implementation of this row in this
                                      * file's history, after `profileUrl`-only
                                      * links and the dots. There is one on the
                                      * Drop page that works; this is it.
                                      */}
                                    <PlatformGlyph platform={sc.platform} />
                                    <span className="pub-hero__social-name">{sc.platform}</span>
                                    {sc.followerCount !== undefined && <b>{formatCompactNumber(sc.followerCount)}</b>}
                                </Chip>
                            );
                        })}
                        {followSlug &&
                            <DropFollowButton
                                slug={followSlug}
                                variant="hero"
                                onLoginRequired={onLoginRequired}
                                referralUserId={referralUserId}
                            />
                        }
                    </div>
                }
            </div>

            <div className="pub-hero__body">
                <div>
                    {/* No pill rather than an empty one — the flag has a solid
                        accent background, so rendering it blank leaves a
                        coloured stub floating over the art. */}
                    {flagLabel(release.status, release.releaseDate) &&
                        <span className="pub-hero__flag">
                            {flagLabel(release.status, release.releaseDate)}
                        </span>
                    }

                    {/*
                      * The wordmark, or the title as type — but never the
                      * IDENTITY name a second time.
                      *
                      * `gameTitle` IS the Drop's name — there is no second
                      * title field for it to disagree with. When a release
                      * carried one, a franchise rendered "CALL OF DUTY" in 80px
                      * directly under the "CALL OF DUTY" already in the
                      * identity block. Two of the same name is not a hero, it
                      * is a bug that looks like a layout.
                      */}
                    {/*
                      * ALWAYS an <h1>, always VISIBLE, whatever the Drop has.
                      *
                      * The anchor wraps the lockup in a heading
                      * (publisher-drop.html:515) and this rendered a bare <img>,
                      * so any Drop with a wordmark had no h1 to navigate by.
                      *
                      * It was then briefly hidden when the title merely repeated
                      * the identity name — which is EVERY franchise Drop, and it
                      * took away the only focal point those pages have. The
                      * anchor prints the name in the identity block AND the
                      * lockup at size, so the repetition it was avoiding is what
                      * the design does on purpose: the small one identifies, the
                      * large one is the page.
                      */}
                    <h1 className="pub-hero__heading">
                        {lockupSrc && !markFailed
                            ? <img
                                className="pub-hero__logo"
                                src={lockupSrc}
                                alt={gameTitle}
                                onError={() => setMarkFailed(true)}
                            />
                            : <span className="pub-hero__title">{gameTitle}</span>}
                    </h1>

                    {tagline && <div className="pub-hero__tagline">{tagline}</div>}
                    {synopsis && <p className="pub-hero__synopsis">{synopsis}</p>}

                    {/*
                      * The CTA is NOT launch furniture — only its wording is.
                      *
                      * Somewhere to buy or play is true of a shipped game as
                      * much as an unreleased one; "Pre-order" simply becomes
                      * "Get the game". Gating the whole row on a release hid the
                      * buy button on every launched title, which is the one
                      * control a publisher most wants on the page.
                      *
                      * The COUNTDOWN is genuinely launch-only and keeps its own
                      * condition inside.
                      */}
                    {(days !== null || buyHref) &&
                    <div className="pub-hero__row">
                        {days !== null &&
                            <span className="pub-hero__countdown">
                                <b>{days}</b>
                                <span>days<br />to launch</span>
                            </span>
                        }
                        {/* Commerce lives with the publisher — we link out, we don't sell. */}
                        {buyHref &&
                            <a className="pub-hero__cta" href={buyHref} target="_blank" rel="noopener noreferrer">
                                {/*
                                  * The wording is the ONLY thing a launch
                                  * changes here. "Pre-order" is only right
                                  * while something is still coming — on a
                                  * shipped title or a franchise with no launch
                                  * at all it asked people to pre-order a game
                                  * they can already buy.
                                  */}
                                {days !== null || release.status === "BETA"
                                    ? "Pre-order ↗"
                                    : "Get the game ↗"}
                            </a>
                        }
                        {/*
                          * No "Watch the trailer" button.
                          *
                          * It sat inches from the trailer itself and did the one
                          * thing the embed exists to prevent: hand the viewer to
                          * YouTube at the exact moment they engaged. The player
                          * to its right IS how you watch it, and a second
                          * control for the same intent — pointing off-site —
                          * competes with the pre-order beside it for no gain.
                          *
                          * `trailerUrl` is unaffected: it still feeds the
                          * player, and the poster's play target still opens the
                          * real thing when we cannot embed it.
                          */}
                    </div>
                    }

                    {/*
                      * Platforms are ALWAYS true of a game; the date is the
                      * only launch-specific half of this line, and it drops out
                      * of the join when absent. A shipped title still runs on
                      * PS5 and PC and should say so.
                      */}
                    {(platforms?.length || release.releaseDate) &&
                        <div className="pub-hero__platforms">
                            {[formatDate(release.releaseDate), ...platforms ?? []].filter(Boolean).join(" · ")}
                        </div>
                    }
                </div>

                {/*
                  * Watching is half of what a visitor came to do, so the right
                  * half is a player rather than empty art. Its own component —
                  * it owns which video is featured, whether the embed has
                  * mounted and the runtime the element reported, and none of
                  * that is the hero's business.
                  */}
                <PublisherHeroWatch
                    trailerUrl={trailerUrl}
                    videos={videos}
                    trailerPosterUrl={trailerPosterUrl}
                    gameTitle={gameTitle}
                    releaseStatus={release.status}
                    followSlug={followSlug}
                    watchNote={watchNote}
                />
            </div>
        </section>
    );
}
